// The first thing Locked asks of anyone: a passphrase to seal it with, asked for the first time
// something is to go in. It opens Locked straight after, so whatever was on its way in carries on.
//
// It can't be recovered, and the dialog says so before anything is set: only the passphrase opens
// what Locked holds, so forgetting it means resetting Locked and losing everything in it.
import PropTypes from 'prop-types'
import {useMutation} from '@apollo/client/react'
import {useState} from 'react'

import Dialog from '@mui/material/Dialog'
import ShieldIcon from '@mui/icons-material/ShieldOutlined'

import PassphraseField from './PassphraseField'
import {BaseButton} from '../shared/blocks/Buttons'
import {Text} from '../shared/blocks/Texts'

import {CREATE_PASSPHRASE, UNLOCK_LOCKED} from '../../graphql/mutations/locked'
import {openLocked} from '../../lib/lockedTools'

import './lockedDialog.scss'

// Long enough that it reads as a phrase rather than a PIN, and that the search box, where a hidden
// Locked is opened, can tell it from someone looking for a file.
export const SHORTEST_PASSPHRASE = 8

const PassphraseDialog = ({onClose, onUnlocked}) => {
  const [passphrase, setPassphrase] = useState('')
  const [repeated, setRepeated] = useState('')
  const [failed, setFailed] = useState(false)
  const [createPassphrase, {loading: creating}] = useMutation(CREATE_PASSPHRASE)
  const [unlock, {loading: unlocking}] = useMutation(UNLOCK_LOCKED)
  const tooShort = passphrase.length < SHORTEST_PASSPHRASE
  const mismatched = repeated && repeated !== passphrase

  const handleSubmit = event => {
    event.preventDefault()
    if (tooShort || repeated !== passphrase) return

    setFailed(false)
    createPassphrase({variables: {input: {passphrase}}})
      .then(() => unlock({variables: {input: {passphrase}}}))
      .then(({data}) => {
        openLocked(data.unlockLocked)
        onUnlocked()
      })
      .catch(() => setFailed(true))
  }

  return (
    <Dialog
      open
      onClose={onClose}
      aria-labelledby='passphrase-title'
      slotProps={{paper: {className: 'locked-dialog', component: 'form', onSubmit: handleSubmit}}}>
      <div className='dialog-head'>
        <ShieldIcon />
        <h2 className='dialog-title' id='passphrase-title'>Set a passphrase for Locked</h2>
      </div>
      <Text className='dialog-body'>
        Locked opens with a passphrase only you know. Items you move into it are sealed with it on arrival.
      </Text>

      <PassphraseField
        label='Passphrase'
        value={passphrase}
        onChange={setPassphrase}
        autoComplete='new-password'
        hint={`At least ${SHORTEST_PASSPHRASE} characters. Any phrase you like — length beats punctuation.`}
        autoFocus
      />
      <PassphraseField label='Repeat it' value={repeated} onChange={setRepeated} autoComplete='new-password' />
      {mismatched && <Text className='dialog-error' role='alert'>The two don’t match.</Text>}
      {failed && <Text className='dialog-error' role='alert'>The passphrase couldn’t be set. Try again.</Text>}

      <Text className='dialog-warning'>
        It can’t be recovered. If you forget it, the only way back into Locked is to reset it, which deletes
        everything inside for good. Write it down somewhere safe.
      </Text>

      <div className='dialog-actions'>
        <BaseButton className='dialog-cancel' variant='text' onClick={onClose}>Cancel</BaseButton>
        <BaseButton
          className='dialog-confirm'
          color='secondary'
          type='submit'
          disabled={creating || unlocking || tooShort || repeated !== passphrase}>
          Set passphrase
        </BaseButton>
      </div>
    </Dialog>
  )
}

PassphraseDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  // Once the passphrase is set and Locked is open with it.
  onUnlocked: PropTypes.func.isRequired
}

export default PassphraseDialog
